/**
 * <variant-pills>
 *
 * Lives inside a card's <product-form> (see snippets/card-hover-form.liquid).
 * Maps the selected Size / View option pills to the matching product variant,
 * updating the hidden [name="id"] input (which Dawn's <product-form> submits),
 * the displayed price, and the add-to-cart button's availability.
 *
 * Implemented as a custom element so connectedCallback runs again whenever the
 * featured-carousel filter clones a card back into the DOM.
 */
if (!customElements.get('variant-pills')) {
  customElements.define(
    'variant-pills',
    class VariantPills extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.form = this.closest('form');
        this.idInput = this.form ? this.form.querySelector('[name="id"]') : null;
        this.priceEl = this.querySelector('[data-card-price]');
        this.addButton = this.querySelector('.card-hover-panel__add');
        this.addButtonText = this.addButton ? this.addButton.querySelector('span') : null;

        const dataEl = this.querySelector('.card-hover-panel__variants');
        try {
          this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
        } catch (e) {
          this.variants = [];
        }

        this.radios = Array.from(this.querySelectorAll('.card-hover-panel__radio'));
        this.radios.forEach((radio) => {
          radio.addEventListener('change', () => this.onOptionChange());
        });

        // Sync initial state (in case markup and data disagree)
        if (this.radios.length) this.onOptionChange();
      }

      // Ordered list of currently selected option values (by option position)
      get selectedOptions() {
        const positions = [...new Set(this.radios.map((r) => Number(r.dataset.position)))].sort(
          (a, b) => a - b
        );
        return positions.map((pos) => {
          const checked = this.radios.find((r) => Number(r.dataset.position) === pos && r.checked);
          return checked ? checked.value : null;
        });
      }

      onOptionChange() {
        const selected = this.selectedOptions;
        const variant = this.variants.find(
          (v) => v.options.length === selected.length && v.options.every((opt, i) => opt === selected[i])
        );

        this.updatePillStyles();

        if (!variant) {
          this.setUnavailable(window.variantStrings ? window.variantStrings.unavailable : 'Unavailable');
          return;
        }

        if (this.idInput) this.idInput.value = variant.id;
        if (this.priceEl && variant.price) this.priceEl.textContent = variant.price;

        if (variant.available) {
          this.setAvailable();
        } else {
          this.setUnavailable(window.variantStrings ? window.variantStrings.soldOut : 'Sold out');
        }
      }

      updatePillStyles() {
        // The visual selected state is driven by :checked in CSS; nothing needed
        // here, but kept as a hook for future active-state tweaks.
      }

      setAvailable() {
        if (!this.addButton) return;
        this.addButton.removeAttribute('disabled');
        this.addButton.removeAttribute('aria-disabled');
        if (this.addButtonText && this.addButton.dataset.addLabel) {
          this.addButtonText.textContent = this.addButton.dataset.addLabel;
        }
      }

      setUnavailable(message) {
        if (!this.addButton) return;
        this.addButton.setAttribute('disabled', 'disabled');
        this.addButton.setAttribute('aria-disabled', 'true');
        if (this.addButtonText) {
          if (!this.addButton.dataset.addLabel) {
            this.addButton.dataset.addLabel = this.addButtonText.textContent.trim();
          }
          this.addButtonText.textContent = message;
        }
      }
    }
  );
}
